/**
 * Named product update presets for the templated-email composer.
 *
 * The workflow for announcing a feature: design the update in Claude Design →
 * add its smart link slug to `FeatureLinkRedirect.tsx` (FEATURE_LINKS) → add a
 * preset here with the copy in BOTH languages. The super admin then picks the
 * language + preset on the Communications page, uploads the screenshots (the
 * ONLY manual step) and sends. Copy is admin-only data, not UI strings — it
 * deliberately lives here and not in the translation files.
 */

export type PresetListType = 'none' | 'steps' | 'bulletsBoxed' | 'bulletsPlain';

export type PresetLanguage = 'en' | 'nl';

export interface PresetChapter {
  title: string;
  body: string;
  listType: PresetListType;
  /** One item per line; steps may prefix a bold lead-in with "Lead | rest of the step". */
  itemsText: string;
  /** Alt text for the chapter's screenshot; the image itself is uploaded manually. */
  imageAlt: string;
}

/** Everything language-dependent in a preset. */
export interface PresetCopy {
  /** Prefills the card's Title field (the email's hero title). */
  title: string;
  eyebrow: string;
  preheader: string;
  intro: string;
  heroImageAlt: string;
  ctaLabel: string;
  chapters: PresetChapter[];
  closingHeadline: string;
  closingButtonLabel: string;
}

export interface ProductUpdatePreset {
  key: string;
  /** Shown on the preset picker button. */
  name: string;
  /** The smart link — /features/<slug> must exist in FeatureLinkRedirect.tsx. Same URL for both languages. */
  ctaUrl: string;
  copy: Record<PresetLanguage, PresetCopy>;
}

export const PRODUCT_UPDATE_PRESETS: ProductUpdatePreset[] = [
  {
    key: 'live-cost-percentage',
    name: 'Approach',
    ctaUrl: 'https://culinarycloud.nl/features/live-cost-percentage',
    copy: {
      en: {
        title: 'Monitor your cost percentage live',
        eyebrow: 'Product update',
        preheader: 'Your cost percentage is always up to date in Culinary Cloud. How does it work?',
        intro: [
          '**Cost percentage → Always up to date in Culinary Cloud. How does it work?**',
          'Without you having to upload or submit information manually, Culinary Cloud calculates your cost percentage based on:\n1) The price of your sold dishes **from your POS system**\n2) The price of your purchased ingredients **according to your invoices**\n3) The quantity of each product **you actually use** in your dishes according to your **product utilization**.',
          '**But how does this process work?**',
        ].join('\n\n'),
        heroImageAlt: 'The Cost Price Analysis page with current cost percentages per dish',
        ctaLabel: '',
        chapters: [
          {
            title: '1. Your POS connection keeps your sales data up to date automatically',
            body: 'This gives you a clear overview of all your sales in the **Revenue** tab, where you can view your sales data by period and split into **All/Dishes/Drinks**.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The Revenue tab with sales by period and All, Dishes and Drinks filters',
          },
          {
            title: '2. Your email connection keeps all your invoices up to date too',
            body: 'This gives you a clear overview of all your purchases in the **Purchases** tab, where you can view your purchase data by period and split into **All/Dishes/Drinks**.\nWant to see only Meat / Fish / Vegetables or another category when talking to suppliers? Use the **Category filter**!',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The Purchases tab with the Category filter highlighted',
          },
          {
            title: '3. Is your supplier charging a different price than before or than agreed?',
            body: 'No problem. Culinary Cloud automatically tracks this in the **Price Alerts** tab.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The Price Alerts tab with price changes per supplier and product',
          },
          {
            title: '4. How much of each product do you use in your dishes?',
            body: 'The **Product Utilization** page calculates this for you based on your recipes and your purchase, sales and inventory data.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Product Utilization with purchased quantities, expected usage and potential waste',
          },
          {
            title: '5. Your live cost price',
            body: 'With all this information, Culinary Cloud calculates your cost price **as it is now**, rather than what it once was in Excel!\n\nLet’s look together at how we can keep your actual cost price as low as possible.\n\nAnd if anything is unclear, the **Guided tours** button at the bottom left of the menu is always there to explain each page.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The Guided tours button and welcome tour on the Cost Price Analysis page',
          },
        ],
        closingHeadline: 'Ready?',
        closingButtonLabel: 'Get started!',
      },
      nl: {
        title: 'Je kostenpercentage live monitoren',
        eyebrow: 'Productupdate',
        preheader: 'Je kostenpercentage is altijd up-to-date in Culinary Cloud. Hoe werkt dit eigenlijk?',
        intro: [
          '**Kostenpercentage → Altijd up-to-date in Culinary Cloud, hoe werkt dit eigenlijk?**',
          'Zonder zelf handmatig informatie te uploaden of in te dienen, berekent Culinary Cloud je kostenpercentage op basis van:\n1) De prijs voor je verkochte gerechten **via je kassasysteem**\n2) De prijs voor je ingekochte ingrediënten **volgens je facturen**\n3) De hoeveelheid per product **die je daadwerkelijk gebruikt** voor je gerechten volgens je **productbenutting**.',
          '**Maar hoe werkt dit proces eigenlijk?**',
        ].join('\n\n'),
        heroImageAlt: 'De Kostprijsanalyse-pagina met actuele kostenpercentages per gerecht',
        ctaLabel: '',
        chapters: [
          {
            title: '1. Door een koppeling met je kassasysteem is je verkoopdata automatisch up-to-date',
            body: 'Dit geeft een mooi overzicht van al je verkopen in de **Omzet**-tab, waar je je verkoopdata per periode en verdeeld over **Alle/Gerechten/Dranken** kunt inzien.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De Omzet-tab met verkopen per periode en filters voor Alle, Gerechten en Dranken',
          },
          {
            title: '2. Door een koppeling via je mail komen ook al je facturen up-to-date binnen',
            body: 'Dit geeft een mooi overzicht van al je inkopen in de **Inkoop**-tab, waar je je inkoopdata per periode en verdeeld over **Alle/Gerechten/Dranken** kunt inzien.\nWil je ze alleen voor Vlees / Vis / Groenten of iets anders zien wanneer je met leveranciers in gesprek gaat? Gebruik de **Categorie-filter**!',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De Inkoop-tab met de Categorie-filter uitgelicht',
          },
          {
            title: '3. Wijkt je leverancier af van de vorige of afgesproken prijs?',
            body: 'Geen probleem. In Culinary Cloud wordt dit automatisch bijgehouden in de **Prijswaarschuwingen**-tab.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De Prijswaarschuwingen-tab met prijswijzigingen per leverancier en product',
          },
          {
            title: '4. Hoeveel van ieder product gebruik je voor je gerechten?',
            body: 'Dit berekent de **Productbenutting**-pagina voor je op basis van je recepturen en je inkoop-, verkoop- en voorraaddata.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Productbenutting met ingekochte hoeveelheden, verwacht gebruik en potentiële verspilling',
          },
          {
            title: '5. Je live kostprijs',
            body: 'Met al die informatie berekent Culinary Cloud niet wat je kostprijs ooit in Excel was, maar **wat het live is**!\n\nLaten we samen kijken hoe we je daadwerkelijke kostprijs zo laag mogelijk houden.\n\nEn mocht er iets niet helemaal duidelijk zijn, dan is er altijd nog de **Rondleidingen**-knop linksonder in het menu, die iedere pagina voor je uitlegt.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De Rondleidingen-knop en de welkomststap op de Kostprijsanalyse-pagina',
          },
        ],
        closingHeadline: 'Klaar voor?',
        closingButtonLabel: 'Aan de slag!',
      },
    },
  },
  {
    key: 'product-utilization',
    name: 'Product Utilization',
    ctaUrl: 'https://culinarycloud.nl/features/product-utilization',
    copy: {
      en: {
        title: 'Product Utilization is live',
        eyebrow: 'New feature',
        preheader: 'See exactly how much of every product you actually use — and what goes to waste.',
        intro:
          'We just launched **Product Utilization** in your Culinary Cloud account. It shows, per product, how much you purchase, how much ends up in your dishes, and how much slips away as waste.\n\nIt works with the data you already have — no extra input needed.',
        heroImageAlt: 'The Product Utilization page',
        ctaLabel: 'Check it out →',
        chapters: [
          {
            title: 'Getting started',
            body: '',
            listType: 'steps',
            itemsText: [
              'Open the page | In the sidebar, go to Cost % and open Utilization.',
              'Pick a period | Choose the weeks you want to analyse.',
              'Review your products | Every purchased product is listed with its usage and waste.',
              'Check the matches | Products are linked to your recipes and cost prices automatically.',
              "Fix what's off | Adjust a match where the link isn't right — it sticks for next time.",
              'Watch the trend | Come back weekly to see utilization improve.',
            ].join('\n'),
            imageAlt: 'Utilization per product',
          },
          {
            title: 'How to read it',
            body: 'For every product you see three numbers: **purchased**, **used in recipes**, and **unaccounted**. The unaccounted share is where your margin leaks.',
            listType: 'bulletsBoxed',
            itemsText: [
              '**Green** — utilization is healthy for this product',
              '**Amber** — worth a look, some volume is unaccounted for',
              '**Red** — a clear leak: purchases far above what your recipes use',
            ].join('\n'),
            imageAlt: 'Utilization detail view',
          },
          {
            title: 'Improve the quality of your analysis',
            body: 'The more complete your recipes and cost prices are, the sharper the analysis gets. **Products linked to recipes** are tracked automatically.\n\nYou can also:',
            listType: 'bulletsPlain',
            itemsText: [
              'Add missing recipes so more usage is captured',
              'Register waste as it happens for a precise picture',
              'Set portion sizes to tighten the numbers',
            ].join('\n'),
            imageAlt: '',
          },
        ],
        closingHeadline: 'Ready to see your product utilization?',
        closingButtonLabel: 'Open Product Utilization',
      },
      nl: {
        title: 'Product Utilization is live',
        eyebrow: 'Nieuwe functie',
        preheader: 'Zie precies hoeveel je van elk product echt gebruikt — en wat er verloren gaat.',
        intro:
          'We hebben zojuist **Product Utilization** gelanceerd in je Culinary Cloud-account. Je ziet per product hoeveel je inkoopt, hoeveel er in je gerechten terechtkomt en hoeveel er verloren gaat als verspilling.\n\nHet werkt met de data die je al hebt — geen extra invoer nodig.',
        heroImageAlt: 'De Product Utilization-pagina',
        ctaLabel: 'Bekijk het →',
        chapters: [
          {
            title: 'Aan de slag',
            body: '',
            listType: 'steps',
            itemsText: [
              'Open de pagina | Ga in de zijbalk naar Cost % en open Utilization.',
              'Kies een periode | Selecteer de weken die je wilt analyseren.',
              'Bekijk je producten | Elk ingekocht product staat er met gebruik en verspilling.',
              'Controleer de koppelingen | Producten worden automatisch gekoppeld aan je recepten en kostprijzen.',
              'Corrigeer wat niet klopt | Pas een koppeling aan waar die niet juist is — dat blijft bewaard.',
              'Volg de trend | Kom wekelijks terug en zie je benutting verbeteren.',
            ].join('\n'),
            imageAlt: 'Benutting per product',
          },
          {
            title: 'Zo lees je het',
            body: 'Voor elk product zie je drie getallen: **ingekocht**, **gebruikt in recepten** en **niet verklaard**. Het niet-verklaarde deel is waar je marge weglekt.',
            listType: 'bulletsBoxed',
            itemsText: [
              '**Groen** — de benutting van dit product is gezond',
              '**Oranje** — het bekijken waard, een deel van het volume is niet verklaard',
              '**Rood** — een duidelijk lek: inkoop ver boven wat je recepten gebruiken',
            ].join('\n'),
            imageAlt: 'Detailweergave benutting',
          },
          {
            title: 'Verbeter de kwaliteit van je analyse',
            body: 'Hoe completer je recepten en kostprijzen, hoe scherper de analyse. **Producten gekoppeld aan recepten** worden automatisch gevolgd.\n\nJe kunt ook:',
            listType: 'bulletsPlain',
            itemsText: [
              'Ontbrekende recepten toevoegen zodat meer gebruik wordt geregistreerd',
              'Verspilling direct registreren voor een precies beeld',
              'Portiegroottes instellen om de cijfers aan te scherpen',
            ].join('\n'),
            imageAlt: '',
          },
        ],
        closingHeadline: 'Klaar om je productbenutting te zien?',
        closingButtonLabel: 'Open Product Utilization',
      },
    },
  },
  {
    key: 'invoice-tool',
    name: 'Facturentool',
    ctaUrl: 'https://culinarycloud.nl/features/invoice-tool',
    copy: {
      en: {
        title: 'The invoice tool is live!',
        eyebrow: 'New feature',
        preheader: 'Upload all your invoices and keep an overview of every purchase in one place.',
        intro:
          'We just gave the **invoice tool** a brand new look. Upload all your invoices and let Culinary Cloud sort everything out for you. Get an overview of all your spending and an easier export button to see it all in one Excel file. Ideal for your bookkeeping!',
        heroImageAlt: 'The invoice tool page',
        ctaLabel: 'Check it out →',
        chapters: [
          {
            title: 'Uploading invoices',
            body: '',
            listType: 'steps',
            itemsText: [
              'Click **Upload invoices**',
              'Upload as many invoices as you like and click **Process invoices**',
            ].join('\n'),
            imageAlt: 'Processing invoices',
          },
          {
            title: 'Your purchase overview',
            body: '',
            listType: 'steps',
            itemsText: [
              'Open **Purchases** from the left menu',
              'Filter by Dishes, Drinks, Non-food or by period',
              'Click any invoice to open it',
            ].join('\n'),
            imageAlt: 'The purchase overview with all your invoices',
          },
          {
            title: 'Every invoice in detail',
            body: 'A clear view of each individual invoice: supplier, order number, VAT rate, the source file and every product you ordered.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Detail view of an invoice',
          },
          {
            title: 'Export to Excel',
            body: 'Or use the **Export button** to see the whole overview in Excel.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Excel export: overview of all your orders',
          },
          {
            title: 'Everything in one overview',
            body: 'A second tab gives you every product per order — article number, category, quantity and price. A clear insight into each unique invoice **plus** an overview of all your invoices and spending, including and excluding VAT.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Excel export: product details',
          },
        ],
        closingHeadline: 'Stop losing time on processing invoices and keeping the books. All in 1 tool!',
        closingButtonLabel: 'Open the invoice tool',
      },
      nl: {
        title: 'De facturentool is live!',
        eyebrow: 'Nieuwe functie',
        preheader: 'Upload al je facturen en houd een overzicht over al je inkopen op dezelfde plek.',
        intro:
          'We hebben zojuist de **Facturentool** in een nieuw jasje gestoken. Upload al je facturen en laat Culinary Cloud alles voor je uitsorteren. Krijg een overzicht van al je uitgaven en een makkelijker export knop om alles in 1 Excel document te bekijken. Ideaal voor je boekhouding!',
        heroImageAlt: 'De facturentool-pagina',
        ctaLabel: 'Bekijk het →',
        chapters: [
          {
            title: 'Facturen uploaden',
            body: '',
            listType: 'steps',
            itemsText: [
              'Klik op **Facturen uploaden**',
              'Upload zo veel facturen als je wilt en klik op **Facturen verwerken**',
            ].join('\n'),
            imageAlt: 'Facturen verwerken',
          },
          {
            title: 'Je inkoopoverzicht',
            body: '',
            listType: 'steps',
            itemsText: [
              'Open **Inkoop** via het linkermenu',
              'Filter op Gerechten, Dranken, Non-food of op periode',
              'Klik op een van de facturen om hem in te zien',
            ].join('\n'),
            imageAlt: 'Het inkoopoverzicht met al je facturen',
          },
          {
            title: 'Iedere factuur in detail',
            body: 'Een duidelijk beeld van elke losse factuur: leverancier, ordernummer, BTW-tarief, het bronbestand en elk besteld product.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Detailweergave van een factuur',
          },
          {
            title: 'Exporteer naar Excel',
            body: 'Of gebruik de **Exporteer knop** om het gehele overzicht in Excel te bekijken.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Excel-export: overzicht van al je orders',
          },
          {
            title: 'Alles in één overzicht',
            body: 'Een tweede tabblad geeft je alle producten per order — artikelnummer, categorie, aantal en prijs. Een duidelijk inzicht in iedere unieke factuur **plus** een overzicht over al je facturen en uitgaven, inclusief en exclusief BTW.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Excel-export: details per product',
          },
        ],
        closingHeadline: 'Verlies geen tijd meer aan het verwerken van facturen en het bijhouden van de boekhouding. Alles in 1 tool!',
        closingButtonLabel: 'Open de Facturentool',
      },
    },
  },
  {
    key: 'guided-tour',
    name: 'Rondleiding',
    ctaUrl: 'https://culinarycloud.nl/features/guided-tour',
    copy: {
      en: {
        title: 'The guided tour is live!',
        eyebrow: 'New feature',
        preheader: 'Discover every button and number in your Culinary Cloud portal with the new guided tour.',
        intro:
          'We just added the **Guided tour** to the dashboard.\nNot quite finding your way, or want to know more about all the buttons and numbers in your Culinary Cloud portal?\nClick the **Guided tours** button on the left side of your screen.',
        heroImageAlt: 'The dashboard with Guided tours in the sidebar',
        ctaLabel: 'Check it out →',
        chapters: [
          {
            title: 'Following the tour',
            body: '',
            listType: 'bulletsPlain',
            itemsText: [
              'Click **Guided tours** on the page you want a tour of.',
              'Click **Show me around!**',
            ].join('\n'),
            imageAlt: 'The tour welcome screen',
          },
          {
            title: 'Your tour of the dashboard',
            body: '',
            listType: 'bulletsPlain',
            itemsText: 'Read the explanation and follow the steps by clicking **Next**.',
            imageAlt: 'A tour step on the dashboard',
          },
          {
            title: 'A tour of another page',
            body: 'Want a tour of another page, like **Cost Price**?',
            listType: 'bulletsPlain',
            itemsText: 'Click **Cost Price** in the menu and follow the steps.',
            imageAlt: 'Cost Price in the menu',
          },
          {
            title: 'The Cost Price tour starts',
            body: 'Follow the steps with **Next**.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The tour on the Cost Price page',
          },
          {
            title: 'Seen enough?',
            body: 'Choose **Skip for now** to leave the tour whenever you like.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Leaving the tour',
          },
        ],
        closingHeadline: 'Become a true Culinary Cloud expert',
        closingButtonLabel: 'Take the tour',
      },
      nl: {
        title: 'De digitale rondleiding staat live!',
        eyebrow: 'Nieuwe functie',
        preheader: 'Ontdek alle knoppen en cijfers in je Culinary Cloud portaal met de nieuwe rondleiding.',
        intro:
          'We hebben zojuist de **Rondleiding** toegevoegd aan het dashboard.\nKom je er niet helemaal uit of wil je meer weten over alle knoppen en cijfers binnen je Culinary Cloud portaal?\nKlik nu op de **Rondleiding** knop aan de linkerkant van je scherm.',
        heroImageAlt: 'Het dashboard met Rondleidingen in het zijmenu',
        ctaLabel: 'Bekijk het →',
        chapters: [
          {
            title: 'De rondleiding volgen',
            body: '',
            listType: 'bulletsPlain',
            itemsText: [
              'Klik op **Rondleiding** vanuit de pagina waar je een rondleiding wilt volgen.',
              'Klik op **Laat me rondkijken**.',
            ].join('\n'),
            imageAlt: 'Het welkomstscherm van de rondleiding',
          },
          {
            title: 'Je rondleiding in het dashboard',
            body: '',
            listType: 'bulletsPlain',
            itemsText: 'Lees de uitleg en volg de stappen door te klikken op **Volgende**.',
            imageAlt: 'Een stap van de rondleiding op het dashboard',
          },
          {
            title: 'De rondleiding in een andere pagina',
            body: 'Wens je een rondleiding in een andere pagina, zoals die van de **Kostprijsanalyse**?',
            listType: 'bulletsPlain',
            itemsText: 'Klik op **Kostprijsanalyse** in het menu en volg de stappen.',
            imageAlt: 'Kostprijsanalyse in het menu',
          },
          {
            title: 'De rondleiding binnen Kostprijsanalyse gaat van start',
            body: 'Volg de stappen via **Volgende**.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De rondleiding op de Kostprijsanalyse-pagina',
          },
          {
            title: 'Genoeg gezien?',
            body: 'Kies voor **Overslaan** en verlaat de rondleiding wanneer je wilt.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De rondleiding verlaten',
          },
        ],
        closingHeadline: 'Word een echte expert in Culinary Cloud',
        closingButtonLabel: 'Volg de rondleiding',
      },
    },
  },
  {
    key: 'support',
    name: 'Support',
    ctaUrl: 'https://culinarycloud.nl/features/support',
    copy: {
      en: {
        title: 'The Support section is live!',
        eyebrow: 'New feature',
        preheader: 'Ask a question, request a feature or share feedback directly in Culinary Cloud.',
        intro:
          'We just added the **Support** section to your dashboard. Need help, have a request or want to share feedback?\n\nClick **Support** in the sidebar. You can ask for help, report a problem, request a feature or share an idea — all in one place.',
        heroImageAlt: 'The Support page with help, feedback, guides and frequently asked questions',
        ctaLabel: 'Open Support →',
        chapters: [
          {
            title: '1. Send us a message',
            body: 'Choose one of the buttons under **Need support or have feedback?** A form opens on the right side of your screen. Select the restaurant your message is about, describe your question or idea, and add a screenshot if helpful.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The support form with restaurant selection, message and attachment fields',
          },
          {
            title: '2. Find an explanation',
            body: 'Open **Guides** to find explanations of the pages and features in Culinary Cloud. Search for the topic you need help with and open its guide.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The Guides page with search and guides for each feature',
          },
          {
            title: '3. Explore the Orders guide',
            body: 'For example, open the **Orders** guide. Read the explanation, choose **Start walkthrough** on a computer for an interactive tour, or go straight to the feature with **Open page →**.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The Orders guide with its explanation, tour and page link',
          },
          {
            title: '4. Get quick answers',
            body: 'Open **Frequently asked questions** to browse answers to common questions about your account, your data and using Culinary Cloud.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Frequently asked questions with categories and expandable answers',
          },
          {
            title: '5. Follow your conversations',
            body: 'Open **Support conversations** to find your earlier messages, including support questions. Read our replies and continue the conversation from there.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Support conversations with previous conversations and replies',
          },
        ],
        closingHeadline: 'Need a hand? Find the help you need in Support.',
        closingButtonLabel: 'Open Support',
      },
      nl: {
        title: 'De supportsectie staat live!',
        eyebrow: 'Nieuwe functie',
        preheader: 'Stel je vraag, vraag een functie aan of deel je feedback via Culinary Cloud.',
        intro:
          'We hebben zojuist de **Support**-sectie toegevoegd aan je dashboard. Hulp nodig, een verzoek of feedback die je graag wilt delen?\n\nKlik op **Support** in het zijmenu. Stel je vraag, meld een probleem, vraag een functie aan of deel je idee — alles op één plek.',
        heroImageAlt: 'De supportpagina met hulp, feedback, uitleg en veelgestelde vragen',
        ctaLabel: 'Open Support →',
        chapters: [
          {
            title: '1. Stuur ons een bericht',
            body: 'Kies een van de knoppen onder **Hulp nodig of feedback?** Rechts in je scherm opent een formulier. Kies op welk restaurant je bericht betrekking heeft, beschrijf je vraag of idee en voeg eventueel een screenshot toe.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Het supportformulier met restaurantkeuze, bericht en bijlagen',
          },
          {
            title: '2. Uitleg nodig?',
            body: 'Klik op **Uitleg** om de **Gidsen** te openen. Zoek de pagina of functie waar je vragen over hebt en open de bijbehorende gids.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De Gidsen-pagina met zoekfunctie en uitleg per functie',
          },
          {
            title: '3. Bekijk bijvoorbeeld de Inkoop-gids',
            body: 'Open bijvoorbeeld de gids voor **Inkoop**. Lees de uitleg, kies op een computer **Rondleiding starten** voor een interactieve rondleiding of ga direct naar de functie via **Pagina openen →**.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De Inkoop-gids met uitleg, rondleiding en link naar de pagina',
          },
          {
            title: '4. Vind snel een antwoord',
            body: 'Open **Veelgestelde vragen** voor antwoorden op veelvoorkomende vragen over je account, je gegevens en het gebruik van Culinary Cloud.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Veelgestelde vragen met categorieën en uitklapbare antwoorden',
          },
          {
            title: '5. Volg je gesprekken',
            body: 'Open **Supportgesprekken** om je eerdere berichten terug te vinden, ook je hulpvragen. Lees onze antwoorden en ga van daaruit verder met het gesprek.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Supportgesprekken met eerdere gesprekken en antwoorden',
          },
        ],
        closingHeadline: 'Hulp nodig? Vind de hulp die je zoekt via Support.',
        closingButtonLabel: 'Open Support',
      },
    },
  },
  {
    key: 'inventory',
    name: 'Voorraad',
    ctaUrl: 'https://culinarycloud.nl/features/inventory',
    copy: {
      en: {
        title: 'The Inventory section is live!',
        eyebrow: 'New feature',
        preheader: 'See what your stock is worth, compare counts and find where value is lost.',
        intro:
          'We just added the **Inventory** section to your dashboard. Count your stock regularly and want to get more insight from it?\n\nClick **Inventory** in the sidebar to see the total value, counted products, unlinked products and what needs ordering. Explore your food and drinks stock and the breakdown by category.',
        heroImageAlt: 'The Inventory overview with stock value, counted products and category breakdown',
        ctaLabel: 'Open Inventory →',
        chapters: [
          {
            title: '1. Understand your leakage',
            body: 'Open the **Leakage** tab to compare your counted stock with the expected stock. Culinary Cloud uses your inventory counts, purchases, recipes and dishes sold to analyse the difference. You can see the estimated loss and an indication of how reliable the analysis is.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The Leakage tab with expected stock, counted stock, differences and analysis confidence',
          },
          {
            title: '2. Look back at earlier counts',
            body: 'Open **History** to see your previous inventory counts and their values, and follow how your stock changes over time.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The History tab with previous inventory counts and their values',
          },
          {
            title: '3. Compare counts',
            body: 'Return to **Count Review**. Use the selector at the top right to choose the count you want to view and the earlier count you want to compare it with.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The count selector with the current count and comparison count',
          },
          {
            title: '4. See the value of each section',
            body: 'Choose a recent drinks count to see the value of each section. For example, view the stock in your wine cabinets, cellar, fridges and main bar.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'A drinks inventory count with stock value per section',
          },
          {
            title: '5. Export to Excel',
            body: 'With a specific inventory selected, click **Export** at the top right and choose **Excel (.xlsx)**. The file is saved to your downloads, ready to open or share.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The inventory Excel export with stock values, sections and product details',
          },
          {
            title: '6. Explore a section',
            body: 'Return to the overview and open a section, such as your cellar. See its products, counted quantities and stock value in one place.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'An inventory section with products, quantities and stock values',
          },
          {
            title: '7. Check products that need attention',
            body: 'Scroll down to find products that are unlinked, not set up or missing a price. Update them while you are there to improve your inventory overview.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Products needing attention because of missing links, setup or prices',
          },
          {
            title: '8. Missed a product while counting?',
            body: 'Use **Add product count** in the section to add a missing product to the existing count. Choose the product, enter the counted quantity and click **Add to count**.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'The Add product count form for adding a missing product to an existing count',
          },
        ],
        closingHeadline: 'Ready to get more insight into your inventory?',
        closingButtonLabel: 'Open Inventory',
      },
      nl: {
        title: 'De voorraadsectie staat live!',
        eyebrow: 'Nieuwe functie',
        preheader: 'Bekijk wat je voorraad waard is, vergelijk tellingen en ontdek waar waarde verloren gaat.',
        intro:
          'We hebben zojuist de **Voorraad**-sectie toegevoegd aan je dashboard. Houd je periodiek je voorraad bij en wil je meer inzicht uit je tellingen halen?\n\nKlik op **Voorraad** in het zijmenu en bekijk de totale waarde, getelde producten, niet-gekoppelde producten en wat besteld moet worden. Je ziet je voorraad opgesplitst in drank en voeding, met een overzicht per categorie.',
        heroImageAlt: 'Het voorraadoverzicht met voorraadwaarde, getelde producten en categorieën',
        ctaLabel: 'Open Voorraad →',
        chapters: [
          {
            title: '1. Krijg inzicht in je derving',
            body: 'Open de tab **Derving** om je getelde voorraad te vergelijken met de verwachte voorraad. Culinary Cloud gebruikt je voorraadtellingen, inkopen, recepturen en verkochte gerechten om het verschil te analyseren. Je ziet de geschatte derving en een indicatie van de betrouwbaarheid van de analyse.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De tab Derving met verwachte en getelde voorraad, verschillen en betrouwbaarheid',
          },
          {
            title: '2. Bekijk eerdere tellingen',
            body: 'Open **Geschiedenis** om je eerdere voorraadtellingen en hun waarden te bekijken. Zo volg je hoe je voorraad zich in de loop van de tijd ontwikkelt.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De tab Geschiedenis met eerdere voorraadtellingen en hun waarden',
          },
          {
            title: '3. Vergelijk tellingen',
            body: 'Ga terug naar **Telling bekijken**. Kies rechtsboven welke telling je wilt inzien en met welke eerdere telling je deze wilt vergelijken.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De selectie van de huidige telling en de telling waarmee je vergelijkt',
          },
          {
            title: '4. Bekijk de waarde per sectie',
            body: 'Kies een recente drankentelling om de waarde per sectie te zien. Bekijk bijvoorbeeld de voorraad in je wijnkasten, kelder, koelkasten en hoofdbar.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Een drankentelling met de voorraadwaarde per sectie',
          },
          {
            title: '5. Exporteer naar Excel',
            body: 'Selecteer een specifieke voorraad, klik rechtsboven op **Exporteren** en kies **Excel (.xlsx)**. Het bestand komt in je downloads terecht, klaar om te openen of te delen.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'De Excel-export met voorraadwaarden, secties en productdetails',
          },
          {
            title: '6. Open een sectie',
            body: 'Ga terug naar het overzicht en open een sectie, bijvoorbeeld je kelder. Bekijk de producten, getelde hoeveelheden en voorraadwaarde op één plek.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Een voorraadsectie met producten, hoeveelheden en voorraadwaarden',
          },
          {
            title: '7. Controleer producten die aandacht nodig hebben',
            body: 'Scroll naar beneden voor producten die niet gekoppeld of niet ingesteld zijn, of waarvan de prijs ontbreekt. Werk ze meteen bij voor een beter voorraadoverzicht.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Producten met ontbrekende koppelingen, instellingen of prijzen',
          },
          {
            title: '8. Een product vergeten tijdens het tellen?',
            body: 'Gebruik **Producttelling toevoegen** in de sectie om een ontbrekend product aan de bestaande telling toe te voegen. Kies het product, vul de getelde hoeveelheid in en klik op **Toevoegen aan telling**.',
            listType: 'none',
            itemsText: '',
            imageAlt: 'Het formulier Producttelling toevoegen voor een ontbrekend product in een bestaande telling',
          },
        ],
        closingHeadline: 'Klaar om meer inzicht te krijgen in je voorraad?',
        closingButtonLabel: 'Open Voorraad',
      },
    },
  },
];
